import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import { axios } from '@/lib/axios';
import { networkDelay } from '@/test/mocks/utils';

import type { SessionContext } from '../types';

// service-user wraps every parent-connect response in the standard
// `{ status, responseBody, ... }` envelope; the axios response
// interceptor in `lib/axios.ts` already unwraps the outer HTTP
// response, so we type the inner envelope here.
type ServiceUserEnvelope<T> = {
  message?: string;
  status?: string;
  responseBody?: T;
  errorCode?: number;
  errorMessage?: string | null;
};

const ENDPOINT = '/user/v1/trainer-slots/parent-connect/session';

export const getParentConnectSessionContext = async (
  sessionId: string
): Promise<SessionContext> => {
  const data: ServiceUserEnvelope<SessionContext> = await axios.get(ENDPOINT, {
    params: { session_id: sessionId },
  });
  // The envelope's `responseBody` is the canonical empty shape if the
  // backend ever returns a malformed payload; the page treats every
  // string field as optional and falls back per-field, so we keep the
  // defaults minimal here rather than risking a misleading partial.
  return (
    data?.responseBody ?? {
      session_id: sessionId,
      variant_id: '',
      batch_id: '',
      batch_name: '',
      teacher_id: '',
      teacher_name: '',
      student_name: '',
      start_time: 0,
      end_time: 0,
      session_status: '',
      reschedule_request_count: 0,
      parent_reschedule_request_count: 0,
      last_rescheduled_by: '',
      last_rescheduled_role: '',
      customer_timezone: 'Asia/Kolkata',
    }
  );
};

export const getParentConnectSessionContextMockHandler = http.get(
  `${env.API_URL}${ENDPOINT}`,
  async ({ request }) => {
    await networkDelay();
    const url = new URL(request.url);
    const sessionId = url.searchParams.get('session_id') ?? 'mock-session-id';
    const nowEpoch = Math.floor(Date.now() / 1000);
    const startEpoch = nowEpoch + 36 * 60 * 60; // ~1.5 days out
    return HttpResponse.json<ServiceUserEnvelope<SessionContext>>({
      status: 'SUCCESS',
      message: 'Parent-connect session context fetched',
      errorCode: 0,
      errorMessage: null,
      responseBody: {
        session_id: sessionId,
        variant_id: 'mock-variant-id',
        batch_id: 'mock-batch-id',
        batch_name: 'Mock Math Batch',
        teacher_id: 'mock-teacher-id',
        teacher_name: 'Mock Trainer',
        student_name: 'Mock Student',
        start_time: startEpoch,
        end_time: startEpoch + 1800,
        session_status: 'pending',
        reschedule_request_count: 1,
        // Below the cap so the mock renders the bookable form by default.
        // Bump to 2 to exercise the "reschedule limit reached" banner.
        parent_reschedule_request_count: 0,
        last_rescheduled_by: 'mock-teacher-id',
        last_rescheduled_role: 'trainer',
        customer_timezone: 'Asia/Kolkata',
      },
    });
  }
);
